import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DATE_FORMAT } from 'src/app/shared/constants';
import {
    DxButtonModule,
    DxDateBoxModule,
    DxFormModule,
    DxLoadPanelModule,
    DxRadioGroupModule,
    DxScrollViewModule,
    DxSelectBoxModule,
    DxTextAreaModule,
    DxTextBoxModule,
    DxToolbarModule,
    DxValidationGroupModule,
    DxValidatorModule,
} from 'devextreme-angular';
import { DxCheckBoxModule } from 'devextreme-angular/ui/check-box';
import { AppointmentsClient } from '@services/web-api-client';

@Component({
    selector: 'app-appointment-date-time',
    imports: [
        CommonModule,
        DxToolbarModule,
        DxButtonModule,
        DxLoadPanelModule,
        DxValidationGroupModule,
        DxTextBoxModule,
        DxValidatorModule,
        DxSelectBoxModule,
        DxDateBoxModule,
        DxScrollViewModule,
        DxFormModule,
        DxRadioGroupModule,
        DxTextAreaModule,
        DxCheckBoxModule,
    ],
    templateUrl: './appointment-date-time.component.html',
})
export class AppointmentDateTimeComponent implements OnInit {
    @Input() date: Date | undefined;
    @Input() Id: string | undefined;
    @Output() dateChange = new EventEmitter<Date>();
    @Input() isReadOnly = false;
    @Input() labelPrefix = '';
    @Input() showAppointmentCount = false;
    @Input() applyStandardTimeLimits = true;

    public dateFormat = DATE_FORMAT;
    public minTime = new Date(new Date().setHours(5, 0, 0));
    public maxTime = new Date(new Date().setHours(23, 0, 0));
    public minDate = new Date();
    public appointmentCount = 0;

    private appointmentsClient = inject(AppointmentsClient);

    ngOnInit(): void {
        if (this.date === undefined) {
            this.setDate();
            this.SetDateTimeLimits();
            this.valueChanged();
        } else if (!this.isReadOnly) {
            this.onDateChanged();
        }
        this.onTimeChanged();
    }

    setDate() {
        const d = new Date();
        let dayOffset = 1;

        if(d.getHours() > 15 || (d.getHours() === 15 && d.getMinutes() >= 1)) {
            dayOffset = 2;
        }

        d.setDate(d.getDate() + dayOffset);
        this.minDate = d;
        d.setHours(6, 0, 0, 0);
        this.date = d;
    }

    SetDateTimeLimits() {
        if (!this.date) return;

        const currentDate = this.date as Date;
        const { minHour, maxHour, maxMin } = this.getTimeLimitsForDate(currentDate);

        this.minTime = new Date(new Date(currentDate).setHours(minHour, 0, 0, 0));
        this.maxTime = new Date(new Date(currentDate).setHours(maxHour, maxMin, 0, 0));
    }

    async onDateChanged() {
        if (!this.date) return;

        const selected = this.date as Date;
        const { minHour, maxHour, maxMin } = this.getTimeLimitsForDate(selected);

        const min = new Date(selected);
        min.setHours(minHour, 0, 0, 0);

        this.minTime = min;
        this.maxTime = new Date(new Date(selected).setHours(maxHour, maxMin, 0, 0));
        this.valueChanged();
    }

    private getTimeLimitsForDate(date: Date) {
        const dayOfWeek = date.getDay();

        let minHour = 4;
        let maxHour = 23;
        let maxMin = 30;

        if (this.applyStandardTimeLimits) {
            const isWeekdayWindow = dayOfWeek >= 2 && dayOfWeek <= 5;
            minHour = isWeekdayWindow ? 5 : 6;
            maxHour = isWeekdayWindow ? 23 : 15;
            maxMin = 0;
        }

        return { minHour, maxHour, maxMin };
    }

    valueChanged() {
        this.dateChange.emit(this.date);
        this.onTimeChanged();
    }

    onTimeChanged() {
        if (this.showAppointmentCount) {
            this.appointmentsClient
                .getAppointmentsCountAt(this.date!, this.Id)
                .subscribe({
                    next: async (data) => {
                        this.appointmentCount = data;
                    },
                    error: () => {
                        this.appointmentCount = 0;
                    },
                });
        }
    }
}
